/**
 * SEOHead — iter-64.
 *
 * Injects per-route SEO metadata (title, description, keywords, og:image)
 * into <head> based on the current pathname + the `seo` section stored in
 * `site_settings` (edited from the staff CMS).
 *
 * Falls back to `seo.default.*` for any missing per-page value, so operators
 * can leave individual page slots empty until they have specific copy to
 * publish. Uses vanilla DOM manipulation instead of react-helmet to keep
 * the dependency graph light.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteConfig, sel } from "../../lib/site-config";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.tagAttrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
}

export default function SEOHead() {
  const cfg = useSiteConfig();
  const loc = useLocation();

  useEffect(() => {
    const seoAll = sel.seo(cfg);
    const defaults = seoAll.default || {};
    const page = (seoAll.pages && seoAll.pages[loc.pathname]) || {};
    const title = page.title || defaults.default_title || "Boulay Beach Resort";
    const description = page.description || defaults.default_description || "";
    const ogImage = page.og_image || defaults.default_og_image || "";
    const keywords = page.keywords || defaults.keywords || "";
    const siteName = defaults.site_name || "Boulay Beach Resort";

    document.title = title;

    upsertMeta('meta[name="description"]', {
      tagAttrs: { name: "description" },
      set: { name: "description", content: description },
    });
    upsertMeta('meta[name="keywords"]', {
      tagAttrs: { name: "keywords" },
      set: { name: "keywords", content: keywords },
    });
    upsertMeta('meta[property="og:title"]', {
      tagAttrs: { property: "og:title" },
      set: { property: "og:title", content: title },
    });
    upsertMeta('meta[property="og:description"]', {
      tagAttrs: { property: "og:description" },
      set: { property: "og:description", content: description },
    });
    upsertMeta('meta[property="og:image"]', {
      tagAttrs: { property: "og:image" },
      set: { property: "og:image", content: ogImage },
    });
    upsertMeta('meta[property="og:site_name"]', {
      tagAttrs: { property: "og:site_name" },
      set: { property: "og:site_name", content: siteName },
    });
    upsertMeta('meta[property="og:type"]', {
      tagAttrs: { property: "og:type" },
      set: { property: "og:type", content: "website" },
    });
    upsertMeta('meta[property="og:url"]', {
      tagAttrs: { property: "og:url" },
      set: { property: "og:url", content: window.location.href },
    });
    upsertMeta('meta[name="twitter:card"]', {
      tagAttrs: { name: "twitter:card" },
      set: { name: "twitter:card", content: "summary_large_image" },
    });
    upsertMeta('meta[name="twitter:title"]', {
      tagAttrs: { name: "twitter:title" },
      set: { name: "twitter:title", content: title },
    });
    upsertMeta('meta[name="twitter:description"]', {
      tagAttrs: { name: "twitter:description" },
      set: { name: "twitter:description", content: description },
    });
    upsertMeta('meta[name="twitter:image"]', {
      tagAttrs: { name: "twitter:image" },
      set: { name: "twitter:image", content: ogImage },
    });

    // Canonical <link>
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.origin + loc.pathname);
  }, [cfg, loc.pathname]);

  return null;
}
